import footerHtml from "../../public/partials/footer.html";

const FOOTER_INIT_FLAG = "__dexFooterBrandInit";

const FOOTER_IMAGE_W = 96;
const FOOTER_IMAGE_VISIBLE_H = 24;

const resolvePublicUrl = (path: string) =>
  new URL(path, document.baseURI || window.location.href).href;

(async () => {
  if ((window as unknown as Record<string, boolean>)[FOOTER_INIT_FLAG]) return;

  const footer = document.querySelector(".dex-site-footer");
  if (!footer) return;

  (window as unknown as Record<string, boolean>)[FOOTER_INIT_FLAG] = true;

  footer.innerHTML = footerHtml;

  const parallaxGroup = document.getElementById("footerBrandParallax");
  const textureImage = document.getElementById("dexisFooterTextureImage");
  if (!parallaxGroup || !textureImage) return;

  const clipShape = document.querySelector<SVGGraphicsElement>(
    "#dexisFooterLogoClip path",
  );
  const clipBox = clipShape?.getBBox();
  const visibleTop = clipBox?.y ?? 0;
  const visibleHeight = clipBox?.height ?? FOOTER_IMAGE_VISIBLE_H;

  const stage = parallaxGroup.closest(".dex-footer-brand__logo-stage") as
    | HTMLElement
    | null;
  if (!stage) return;

  const FOOTER_TEXTURE_FRAMING = {
    parallaxSlackFactor: 3.4,
    parallaxCap: 48,
  } as const;

  const TEXTURE_SRC =
    textureImage.getAttribute("href") ||
    textureImage.getAttributeNS("http://www.w3.org/1999/xlink", "href") ||
    "assets/footer-brand-texture2.webp";

  const loadTextureMetrics = () =>
    new Promise<{ w: number; h: number }>((resolve, reject) => {
      const probe = new Image();
      probe.onload = () =>
        resolve({ w: probe.naturalWidth, h: probe.naturalHeight });
      probe.onerror = () => reject(new Error("footer texture failed to load"));
      probe.src = resolvePublicUrl(TEXTURE_SRC);
    });

  let metrics: { w: number; h: number };
  try {
    metrics = await loadTextureMetrics();
  } catch {
    return;
  }

  const imageWidth = FOOTER_IMAGE_W;
  const imageHeight = imageWidth * (metrics.h / metrics.w);
  const slack = Math.max(0, imageHeight - visibleHeight);

  const shiftExtent =
    Math.min(
      FOOTER_TEXTURE_FRAMING.parallaxCap,
      slack * FOOTER_TEXTURE_FRAMING.parallaxSlackFactor,
      slack * 2,
    ) / 3;
  const SHIFT_TOP = 0;
  const SHIFT_BOTTOM = shiftExtent;

  textureImage.setAttribute("preserveAspectRatio", "xMidYMin meet");

  let imageShiftY = 0;
  let textureStep: "top" | "bottom" = "top";
  let lastScrollY = window.scrollY;

  const applyImageSize = () => {
    textureImage.setAttribute("width", String(imageWidth));
    textureImage.setAttribute("height", String(imageHeight));
    textureImage.setAttribute("x", "0");
    textureImage.setAttribute("y", String(visibleTop - imageShiftY));
  };

  applyImageSize();

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  const LERP_FACTOR = 0.14 / 4.5;
  let currentShift = 0;
  let targetShift = SHIFT_TOP;
  let animating = false;

  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

  const applyParallax = () => {
    imageShiftY = currentShift;
    applyImageSize();
  };

  const animate = () => {
    currentShift = lerp(currentShift, targetShift, LERP_FACTOR);

    if (Math.abs(currentShift - targetShift) < 0.01) {
      currentShift = targetShift;
      animating = false;
    } else {
      requestAnimationFrame(animate);
    }

    applyParallax();
  };

  const startAnimation = () => {
    if (!animating) {
      animating = true;
      requestAnimationFrame(animate);
    }
  };

  const isFooterInView = () => {
    const rect = stage.getBoundingClientRect();
    const wh = window.innerHeight || document.documentElement.clientHeight;
    return rect.bottom > 0 && rect.top < wh;
  };

  const setTextureStep = (step: "top" | "bottom") => {
    if (textureStep === step) return;
    textureStep = step;
    targetShift = step === "bottom" ? SHIFT_BOTTOM : SHIFT_TOP;
    startAnimation();
  };

  const resetTextureStep = () => {
    if (textureStep === "top" && currentShift === SHIFT_TOP) return;
    textureStep = "top";
    targetShift = SHIFT_TOP;
    currentShift = SHIFT_TOP;
    animating = false;
    applyParallax();
  };

  const applyScrollDelta = (delta: number) => {
    if (!isFooterInView()) {
      resetTextureStep();
      return;
    }
    if (shiftExtent <= 0 || delta === 0) return;
    if (delta > 0) setTextureStep("bottom");
  };

  const update = () => {
    const scrollY = window.scrollY;
    const delta = scrollY - lastScrollY;
    lastScrollY = scrollY;
    applyScrollDelta(delta);
  };

  let scrollRaf: number | null = null;
  const onScroll = () => {
    if (scrollRaf !== null) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = null;
      update();
    });
  };

  const onWheel = (event: WheelEvent) => {
    if (event.deltaY === 0) return;
    applyScrollDelta(event.deltaY);
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("wheel", onWheel, { passive: true });
  window.addEventListener("resize", () => {
    applyParallax();
  }, { passive: true });
})();
